/**
 * Shared-promise deduplication for concurrent refresh requests.
 *
 * Ported from the pattern previously duplicated across palm-web,
 * payday-web, pos-web, and planner-web tokenManager modules. Ensures that
 * N concurrent callers of `refreshAccessToken()` fire exactly one network
 * request and all await the same result.
 *
 * A new task may run after the previous one resolves OR rejects — the
 * in-flight slot is always released in the promise's `finally` step.
 *
 * @module @puffin-core/auth-token-storage/refreshCoordinator
 */

/**
 * Create a per-instance refresh coordinator.
 *
 * @returns {{
 *   run: (task: () => Promise<*>) => Promise<*>,
 *   reset: () => void,
 * }}
 */
export function createRefreshCoordinator() {
  let inFlight = null;

  return {
    /**
     * Run `task` if no refresh is in flight; otherwise return the in-flight promise.
     * The shared promise is released once the task resolves or rejects.
     *
     * @param {() => Promise<*>} task - The refresh work to execute
     * @returns {Promise<*>} The task's result (shared with concurrent callers)
     */
    run(task) {
      if (inFlight) {
        return inFlight;
      }
      inFlight = (async () => {
        try {
          return await task();
        } finally {
          inFlight = null;
        }
      })();
      return inFlight;
    },

    /**
     * Drop the in-flight promise without waiting for it to settle. Test-only.
     */
    reset() {
      inFlight = null;
    },
  };
}
